import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import WorkspaceService from "services/workspace-service";
import DataTable from "examples/Tables/DataTable";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const Usage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    rows: [],
    planName: "Loading..."
  });

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        // Fetch entitlements which includes usage, plan, and limits
        const entitlements = await WorkspaceService.fetchWorkspaceEntitlements();

        if (!entitlements) {
          console.error("Empty entitlements response");
          return;
        }

        const limits = entitlements.limits || {};
        const usage = entitlements.usage || {};
        const boosts = entitlements.boosts || {};

        // Helper to format rows
        const createRow = (metric, description, planCount, addonCount, totalAvailable, totalUsed) => ({
          metric: (
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="button" fontWeight="medium" color="text">
                {metric}
              </MDTypography>
              <Tooltip title={description} placement="top" arrow>
                <MDBox display="flex" alignItems="center" ml={0.5} sx={{ cursor: "pointer", color: "text.secondary" }}>
                  <InfoOutlinedIcon fontSize="small" sx={{ width: "16px", height: "16px" }} />
                </MDBox>
              </Tooltip>
            </MDBox>
          ),
          planCount: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {planCount}
            </MDTypography>
          ),
          addonCount: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {addonCount > 0 ? addonCount : "-"}
            </MDTypography>
          ),
          totalAvailable: (
            <MDTypography variant="caption" color="text" fontWeight="bold">
              {totalAvailable}
            </MDTypography>
          ),
          formattedUsed: (
            <MDTypography variant="caption" color={totalUsed >= totalAvailable && totalAvailable !== "-" ? "error" : "text"} fontWeight="bold">
              {totalUsed}
            </MDTypography>
          ),
        });

        const rows = [
          // Profiles
          createRow(
            "Profiles",
            "The total number of social media profiles you can track simultaneously.",
            (limits.profiles || 0) - (boosts.profiles || 0),
            boosts.profiles || 0,
            limits.profiles || 0,
            usage.profiles || 0
          ),
          // Profile Swaps
          createRow(
            "Profile Swaps",
            "The number of times you can remove a profile and add a new one within a billing cycle.",
            limits.profile_swaps || 0,
            0,
            limits.profile_swaps || 0,
            usage.profile_swaps || 0
          ),
        ];

        // Conditionally add Daily AI Prompts
        const hasAIFeature = (entitlements.features || []).some(
          (f) => f === "AI Powered Insight"
        );

        if (hasAIFeature) {
          rows.push(
            createRow(
              "Daily AI Prompts",
              "The number of AI-powered chart analysis questions you can ask per day. Resets daily.",
              limits.ai_prompt_limit_daily || 0,
              0,
              limits.ai_prompt_limit_daily || 0,
              usage.ai_prompt_limit_daily || 0
            )
          );
        }

        // Conditionally add Snapshot rows
        const hasSnapshotFeature = (entitlements.features || []).some(
          (f) => f === "Snapshot"
        );

        if (hasSnapshotFeature) {
          rows.push(
            // Active Snapshots
            createRow(
              "Active Snapshots",
              "The number of recurring snapshot reports you can have active at the same time.",
              (limits.snapshots || 0) - (boosts.snapshots || 0),
              boosts.snapshots || 0,
              limits.snapshots || 0,
              usage.active_snapshots || 0
            ),
            // Lifetime Snapshots (No Limits)
            createRow(
              "Lifetime Snapshots",
              "The total number of snapshot reports generated since your account was created.",
              "-",
              "-",
              "-",
              usage.snapshots || 0
            )
          );
        }

        // Conditionally add Report rows
        const hasReportFeature = (entitlements.features || []).some(
          (f) => f === "Reports" || true // Assuming all plans have reports for now, or check specific feature flag
        );

        if (hasReportFeature) {
          rows.push(
            // Active Reports
            createRow(
              "Active Reports",
              "The number of scheduled reports you can have active at the same time.",
              limits.reports || 0,
              "-", // No boosts for reports yet
              limits.reports || 0,
              usage.active_reports || 0
            ),
            // Lifetime Reports (No Limits)
            createRow(
              "Lifetime Reports",
              "The total number of reports generated since your account was created.",
              "-",
              "-",
              "-",
              usage.reports || 0
            )
          );
        }

        setData({
          rows,
          planName: entitlements.plan || "Basic"
        });

      } catch (error) {
        console.error("Error fetching usage details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsageData();
  }, []);

  const columns = [
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            METRIC
          </MDTypography>
        </MDBox>
      ),
      accessor: "metric",
      align: "left",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            PLAN COUNT
          </MDTypography>
        </MDBox>
      ),
      accessor: "planCount",
      align: "center",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            ADDON COUNT
          </MDTypography>
        </MDBox>
      ),
      accessor: "addonCount",
      align: "center",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            TOTAL AVAILABLE
          </MDTypography>
        </MDBox>
      ),
      accessor: "totalAvailable",
      align: "center",
    },
    {
      Header: (
        <MDBox py={1} px={2}>
          <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
            TOTAL USED
          </MDTypography>
        </MDBox>
      ),
      accessor: "formattedUsed",
      align: "center",
    },
  ];

  return (
    <Card sx={{ mb: 3, mt: 7, overflow: "visible" }}>
      <MDBox
        mx={2}
        mt={-3}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MDTypography variant="h6" color="white">
          Usage Information - {data.planName}
        </MDTypography>
      </MDBox>

      <MDBox p={2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows: data.rows }}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            entriesPerPage={false}
          />
        )}
      </MDBox>
    </Card>
  );
};

export default Usage;